import { useState } from "react"
import { type SocketEnabled, useSocket } from "./useSocket"

type SyncDataOptions<Data, Raw> = {
  isPrivate?: boolean
  eventName?: string | string[]
  enabled?: SocketEnabled<Raw>
  initialValue?: Data
  updater?: (currentData: Data | undefined, eventData: Raw) => Data
  onBeforeSync?: (eventName: string, eventData: Raw) => void
}

/**
 * useSyncData — subscribes to the signage private channel and keeps local
 * state in sync with every event received via listenToAll.
 *
 * Pass `eventName` to only react to a specific event and skip all others.
 * Pass `enabled` as a boolean to gate the connection, or as a predicate
 * `(event, data) => boolean` for fine-grained per-event filtering.
 *
 * @param options.channel      - override resolved channel name
 * @param options.isPrivate    - use private channel (default: true)
 * @param options.eventName    - sync only when one of these event name(s) is received
 * @param options.enabled      - boolean or predicate to control syncing
 * @param options.initialValue - initial state value
 * @param options.updater      - merge incoming event data into existing state
 * @param options.onBeforeSync - called before state is updated
 */
export function useSyncData<Data = unknown, Raw = unknown>(options: SyncDataOptions<Data, Raw>) {
  const [data, setData] = useState<Data | undefined>(options.initialValue)

  const { connected, channelName, error } = useSocket<Raw>({
    isPrivate: options.isPrivate ?? true,
    eventName: options.eventName,
    enabled: options.enabled,
    handler: (incomingEvent, eventData) => {
      options.onBeforeSync?.(incomingEvent, eventData)

      if (options.updater) {
        setData((prevData) => options.updater!(prevData, eventData))
      } else {
        setData(eventData as unknown as Data)
      }
    },
  })

  return { data, setData, connected, channelName, error }
}
